import { useMemo, useRef, useState } from 'react'
import Alert from 'react-bootstrap/Alert'
import Badge from 'react-bootstrap/Badge'
import Button from 'react-bootstrap/Button'
import Card from 'react-bootstrap/Card'
import Dropdown from 'react-bootstrap/Dropdown'
import ListGroup from 'react-bootstrap/ListGroup'
import { Link } from 'react-router-dom'
import { toast } from 'react-toastify'
import { deleteTransaction, type GroupDetail, type Transaction } from '../../../../types'
import { actionApiCall } from '../../api/actionApiCall'
import { useCapabilities } from '../../hooks/useCapabilities'
import { useCurrencies } from '../../hooks/useCurrencies'
import { useReceiptQueue } from '../../hooks/useReceipts'
import { useFilteredTransactions, type TransactionFilters } from '../../hooks/useTransactions'
import { formatDateTime } from '../../utils/format'
import { exponentOf, formatMoney } from '../../utils/money'
import { adresaUpravyUtraty, lzeUpravitPodleUctenky } from '../receipts/ReceiptPage'
import { HistoryFilters } from './HistoryFilters'
import { ReceiptReviewSection } from './ReceiptReviewSection'
import { MAX_FOTEK } from './ReceiptUploadGrouping'
import { ReceiptUploadModal } from './ReceiptUploadModal'
import { TransactionFormModal } from './TransactionFormModal'

const STRANKA = 25

export type HistoryTabProps = {
    group: GroupDetail
    transactions: Transaction[]
    truncated: boolean
    /** Offline se smí jen číst. */
    canWrite: boolean
    onChanged: () => void
}

export function HistoryTab({ group, transactions, truncated, canWrite, onChanged }: HistoryTabProps) {
    const { data: currencies } = useCurrencies()

    const [zobrazeno, setZobrazeno] = useState(STRANKA)
    const [editing, setEditing] = useState<Transaction | undefined>()
    const [showForm, setShowForm] = useState(false)
    const [showFilters, setShowFilters] = useState(false)
    const [filters, setFilters] = useState<TransactionFilters>({})

    // Útraty z účtenek: soubory se vyberou hned po kliknutí, v dialogu se jen
    // seskupí a zvolí, kdo je přečte. Zbytek běží na pozadí.
    const { data: capabilities } = useCapabilities()
    const uctenkyInput = useRef<HTMLInputElement>(null)
    const [uctenky, setUctenky] = useState<File[] | undefined>()

    // Účtenky ke kontrole: nahrané účtenky, ze kterých ještě nevznikla útrata.
    // Rozpoznávání u nich běží na serveru, takže je tu uživatel najde i po
    // zavření aplikace, obnovení stránky nebo restartu serveru.
    const { data: fronta } = useReceiptQueue(group.id, canWrite)

    // Filtrovaný pohled se musí ptát serveru; bez filtru stačí uložený snímek.
    const filtrAktivni = useMemo(
        () => Object.values(filters).some((hodnota) => hodnota !== undefined && hodnota !== ''),
        [filters],
    )
    const { data: filtrovane, isFetching: filtrujeSe } = useFilteredTransactions(
        group.id,
        { ...filters, limit: 200 },
        filtrAktivni && canWrite,
    )

    const seznam = filtrAktivni ? (filtrovane?.items ?? []) : transactions

    const memberName = (memberId: string) => group.members.find((member) => member.id === memberId)?.displayName ?? 'Neznámý'

    const otevriSUctenkami = (files: File[]) => {
        if (files.length === 0) {
            return
        }
        if (files.length > MAX_FOTEK) {
            toast.warning(`Najednou lze nahrát nejvýš ${MAX_FOTEK} fotek, zbytek nahrajte zvlášť.`)
        }
        setShowForm(false)
        setUctenky(files.slice(0, MAX_FOTEK))
    }

    const handleDelete = async (transaction: Transaction) => {
        if (!window.confirm(`Opravdu smazat pohyb „${transaction.title}"?`)) {
            return
        }
        const result = await actionApiCall(() => deleteTransaction({ path: { transactionId: transaction.id } }), {
            successMessage: 'Pohyb byl smazán.',
        })
        if (result !== undefined) {
            onChanged()
        }
    }



    return (
        <>
            <div className="d-flex justify-content-between align-items-center gap-2 mb-3">
                <div className="d-flex gap-2">
                    <Button
                        variant={filtrAktivni ? 'secondary' : 'outline-secondary'}
                        className="su-tappable"
                        disabled={!canWrite && !filtrAktivni}
                        onClick={() => setShowFilters((current) => !current)}
                    >
                        <i className="bi bi-funnel me-1" aria-hidden="true" />
                        Filtr
                        {filtrAktivni && <span className="ms-1">•</span>}
                    </Button>

                    <Dropdown align="start">
                        <Dropdown.Toggle variant="outline-secondary" className="su-tappable" id="menu-export" aria-label="Export historie">
                            <i className="bi bi-download" aria-hidden="true" />
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                            {/* Stahování souboru musí jít přes odkaz, aby ho prohlížeč uložil. */}
                            <Dropdown.Item href={`/api/groups/${group.id}/export.csv`} download>
                                Export do CSV
                            </Dropdown.Item>
                            <Dropdown.Item href={`/api/groups/${group.id}/export.json`} download>
                                Export do JSON
                            </Dropdown.Item>
                        </Dropdown.Menu>
                    </Dropdown>
                </div>

                <div className="d-flex gap-2">
                    {(capabilities?.receiptAnalysis || capabilities?.receiptMcp) && (
                        <>
                            <input
                                ref={uctenkyInput}
                                type="file"
                                accept="image/*,application/pdf"
                                multiple
                                hidden
                                data-testid="uctenky-k-pohybum"
                                onChange={(event) => {
                                    otevriSUctenkami([...(event.target.files ?? [])])
                                    event.target.value = ''
                                }}
                            />
                            <Button
                                variant="outline-primary"
                                className="su-tappable"
                                disabled={!canWrite || group.members.length === 0}
                                aria-label="Přidat z účtenek"
                                title="Přidat útraty z fotek nebo PDF účtenek"
                                onClick={() => uctenkyInput.current?.click()}
                            >
                                <i className="bi bi-camera" aria-hidden="true" />
                                <span className="d-none d-sm-inline ms-1">Z účtenek</span>
                            </Button>
                        </>
                    )}
                    <Button
                        className="su-tappable"
                        disabled={!canWrite || group.members.length === 0}
                        onClick={() => {
                            setEditing(undefined)
                            setShowForm(true)
                        }}
                    >
                        <i className="bi bi-plus-lg me-1" aria-hidden="true" />
                        Přidat pohyb
                    </Button>
                </div>
            </div>

            {canWrite && <ReceiptReviewSection group={group} receipts={fronta ?? []} />}

            {showFilters && (
                <HistoryFilters
                    group={group}
                    filters={filters}
                    onChange={setFilters}
                    onReset={() => {
                        setFilters({})
                        setShowFilters(false)
                    }}
                />
            )}

            {filtrAktivni && (
                <div className="small text-secondary mb-2">
                    {filtrujeSe ? 'Filtruji…' : `Nalezeno ${filtrovane?.total ?? 0} ${pocetPohybu(filtrovane?.total ?? 0)}`}
                </div>
            )}

            {seznam.length === 0 && filtrAktivni && !filtrujeSe && (
                <Card body className="text-secondary">
                    Zadanému filtru neodpovídá žádný pohyb.
                </Card>
            )}

            {seznam.length === 0 && !filtrAktivni && (
                <Card body className="text-secondary">
                    {group.members.length === 0
                        ? 'Nejdřív přidejte do skupiny členy, pak půjde evidovat útraty.'
                        : 'Zatím tu není žádný pohyb. Přidejte první útratu tlačítkem „Přidat pohyb".'}
                </Card>
            )}

            {seznam.length > 0 && (
                <Card>
                    <ListGroup variant="flush">
                        {seznam.slice(0, zobrazeno).map((transaction) => {
                            const exponent = exponentOf(currencies, transaction.currencyCode)
                            const baseExponent = exponentOf(currencies, group.baseCurrencyCode)
                            const jinaMena = transaction.currencyCode !== group.baseCurrencyCode

                            return (
                                <ListGroup.Item key={transaction.id} className="d-flex align-items-start gap-2">
                                    <div className="flex-grow-1 min-w-0">
                                        <div className="d-flex align-items-center gap-2">
                                            {transaction.receiptCount > 0 ? (
                                                <Link
                                                    to={`/skupiny/${group.id}/pohyby/${transaction.id}/uctenka`}
                                                    className="fw-semibold text-truncate text-body text-decoration-none"
                                                >
                                                    {transaction.title}
                                                </Link>
                                            ) : (
                                                <span className="fw-semibold text-truncate">{transaction.title}</span>
                                            )}
                                            {transaction.type === 'TRANSFER' && (
                                                <Badge bg="info" text="dark">
                                                    Úhrada
                                                </Badge>
                                            )}
                                            {transaction.receiptCount > 0 && (
                                                <i
                                                    className="bi bi-paperclip text-secondary"
                                                    title="Pohyb má připojenou účtenku"
                                                    aria-label="Pohyb má připojenou účtenku"
                                                />
                                            )}
                                        </div>
                                        <div className="small text-secondary">
                                            {transaction.type === 'TRANSFER'
                                                ? `${memberName(transaction.payers[0]?.memberId ?? '')} → ${memberName(transaction.shares[0]?.memberId ?? '')}`
                                                : `Zaplatil ${memberName(transaction.payers[0]?.memberId ?? '')} · ${transaction.shares.length} ${pocetOsob(transaction.shares.length)}`}
                                        </div>
                                        <div className="small text-secondary">{formatDateTime(transaction.occurredAt)}</div>
                                    </div>

                                    <div className="text-end flex-shrink-0">
                                        <div className="fw-semibold">{formatMoney(transaction.amountTotal, transaction.currencyCode, exponent)}</div>
                                        {jinaMena && (
                                            <div className="small text-secondary">
                                                {formatMoney(transaction.amountTotalBase, group.baseCurrencyCode, baseExponent)}
                                            </div>
                                        )}
                                    </div>

                                    <Dropdown align="end">
                                        <Dropdown.Toggle
                                            variant="link"
                                            size="sm"
                                            className="text-secondary su-tappable p-1"
                                            aria-label={`Akce pro ${transaction.title}`}
                                            disabled={!canWrite}
                                        >
                                            <i className="bi bi-three-dots-vertical" aria-hidden="true" />
                                        </Dropdown.Toggle>
                                        <Dropdown.Menu>
                                            {/* Útrata z účtenky se prohlíží i upravuje u fotky účtenky,
                                                ostatní pohyby ve formuláři. */}
                                            {transaction.receiptCount > 0 && (
                                                <Dropdown.Item as={Link} to={`/skupiny/${group.id}/pohyby/${transaction.id}/uctenka`}>
                                                    Zobrazit
                                                </Dropdown.Item>
                                            )}
                                            {lzeUpravitPodleUctenky(transaction) ? (
                                                <Dropdown.Item as={Link} to={adresaUpravyUtraty(group.id, transaction.id)}>
                                                    Upravit
                                                </Dropdown.Item>
                                            ) : (
                                                <Dropdown.Item
                                                    onClick={() => {
                                                        setEditing(transaction)
                                                        setShowForm(true)
                                                    }}
                                                >
                                                    Upravit
                                                </Dropdown.Item>
                                            )}
                                            {transaction.receiptCount === 0 && (
                                                <Dropdown.Item as={Link} to={`/skupiny/${group.id}/pohyby/${transaction.id}/uctenka`}>
                                                    Přidat účtenku
                                                </Dropdown.Item>
                                            )}
                                            <Dropdown.Divider />
                                            <Dropdown.Item className="text-danger" onClick={() => void handleDelete(transaction)}>
                                                Smazat
                                            </Dropdown.Item>
                                        </Dropdown.Menu>
                                    </Dropdown>
                                </ListGroup.Item>
                            )
                        })}
                    </ListGroup>

                    {seznam.length > zobrazeno && (
                        <Card.Footer className="text-center">
                            <Button variant="link" onClick={() => setZobrazeno((current) => current + STRANKA)}>
                                Zobrazit další ({seznam.length - zobrazeno})
                            </Button>
                        </Card.Footer>
                    )}
                </Card>
            )}

            {truncated && !filtrAktivni && (
                <Alert variant="secondary" className="mt-3 py-2 small mb-0">
                    Historie je velmi dlouhá, zobrazují se jen novější pohyby.
                </Alert>
            )}

            {showForm && (
                <TransactionFormModal
                    show={showForm}
                    key={editing?.id ?? 'novy'}
                    group={group}
                    transaction={editing}
                    onReceiptFiles={otevriSUctenkami}
                    onClose={() => setShowForm(false)}
                    onSaved={onChanged}
                />
            )}

            {uctenky && <ReceiptUploadModal show group={group} files={uctenky} onClose={() => setUctenky(undefined)} />}
        </>
    )
}

function pocetPohybu(count: number): string {
    if (count === 1) {
        return 'pohyb'
    }
    if (count >= 2 && count <= 4) {
        return 'pohyby'
    }
    return 'pohybů'
}

function pocetOsob(count: number): string {
    if (count === 1) {
        return 'osoba'
    }
    if (count >= 2 && count <= 4) {
        return 'osoby'
    }
    return 'osob'
}
